import { DataInput, DataInputType } from '@/types/automationFields.ts';
import { IntegrationInputContract } from '@/types/integrations.ts';
import { Accordion, Badge, Box, Group, Stack, Text } from '@mantine/core';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import useDataInputOptions, {
  getDataInputOptionValue,
  makeNewInputConfig,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/hooks/useDataInputOptions.ts';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelectWithCustomPills } from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiSelectWithCustomPills.tsx';
import { EditablePill } from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/EditablePill.tsx';
import {
  NO_DATA_INPUTS_DESCRIPTION,
  NO_DATA_INPUTS_PLACEHOLDER,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/constants.ts';
import useRetrieveOutputContracts from '@/hooks/api/steps/useRetrieveOutputContracts.ts';
import DataInputSchemaContracts from '@/pages/Editor/components/SharedFields/DataInputSelector/DataInputSchemaContracts.tsx';
import {
  buildOutputContractByOptionValue,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/dataInputSchemaContractUtils.ts';
import {
  ensureCodeInputBindingNames,
  getCodeInputBindingError,
  getDuplicateBindingIndexes,
  haveCodeInputBindingNamesChanged,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/codeInputBindings.ts';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import { getInputContractStatus } from '@/pages/Editor/components/SharedFields/DataInputSelector/inputContractStatus.ts';

type props = {
  dataInputs: DataInput[];
  stepId: string;
  updateDataInputs: (dataInputs: DataInput[]) => void;
  error?: ReactNode;
  description?: ReactNode;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  requireCodeInputNames?: boolean;
  showCodeInputNameFields?: boolean;
  expandCodeInputNamesOnInputAdd?: boolean;
  codeInputNameErrorsByIndex?: Record<number, string>;
  inputContract?: IntegrationInputContract | null;
};

const CODE_INPUT_NAMES_ACCORDION_VALUE = 'code-input-names';

const MultiDataInputSelectorUI = ({
  dataInputs,
  stepId,
  updateDataInputs,
  error,
  description,
  label,
  placeholder,
  disabled,
  required,
  requireCodeInputNames = false,
  showCodeInputNameFields = requireCodeInputNames,
  expandCodeInputNamesOnInputAdd = false,
  codeInputNameErrorsByIndex = {},
  inputContract,
}: props) => {
  const {
    options,
    isLoading,
    inventoryError,
    unavailableSourceTypes = [],
  } = useDataInputOptions(stepId);
  const {
    data: outputContracts,
    isError: outputContractsError,
    isPending: outputContractsPending,
  } = useRetrieveOutputContracts();
  const [codeInputNamesAccordionValue, setCodeInputNamesAccordionValue] = useState<string | null>(
    null,
  );
  const previousDataInputCountRef = useRef(dataInputs.length);

  const flattenedOptions = useMemo(() => {
    return options.flatMap((group) => group.items);
  }, [options]);
  const hasSelectableOptions = flattenedOptions.length > 0;
  const showUnavailableState =
    !isLoading && !hasSelectableOptions && dataInputs.length === 0 && !!inventoryError;
  const showEmptyState =
    !inventoryError && !isLoading && !hasSelectableOptions && dataInputs.length === 0;

  const selectedOptions = useMemo(() => {
    return dataInputs.map((d) => getDataInputOptionValue(d.config));
  }, [dataInputs]);

  const outputContractByValue = useMemo(() => {
    const upstreamContracts = outputContracts?.upstream_output_contracts?.[stepId] ?? [];
    return buildOutputContractByOptionValue(upstreamContracts);
  }, [outputContracts?.upstream_output_contracts, stepId]);

  const selectedOutputContracts = useMemo(() => {
    const selectedValues = new Set(selectedOptions);

    return Array.from(outputContractByValue.entries())
      .filter(([value]) => selectedValues.has(value))
      .map(([, outputContract]) => outputContract);
  }, [outputContractByValue, selectedOptions]);
  const inputContractStatus = useMemo(
    () =>
      getInputContractStatus(
        inputContract,
        dataInputs.flatMap((dataInput) => {
          const selectedValue = getDataInputOptionValue(dataInput.config);
          if (!selectedValue) {
            return [];
          }
          return [
            {
              sourceType: dataInput.data_input_type,
              outputContract: outputContractByValue.get(selectedValue) ?? null,
              sourceAvailable:
                flattenedOptions.some((option) => option.value === selectedValue) &&
                (dataInput.data_input_type !== DataInputType.Dynamic ||
                  (!outputContractsPending && !outputContractsError)),
            },
          ];
        }),
      ),
    [
      dataInputs,
      flattenedOptions,
      inputContract,
      outputContractByValue,
      outputContractsError,
      outputContractsPending,
    ],
  );

  useEffect(() => {
    if (!requireCodeInputNames || isLoading || inventoryError || dataInputs.length === 0) {
      return;
    }

    const dataInputsWithBindingNames = ensureCodeInputBindingNames(dataInputs, flattenedOptions);
    if (haveCodeInputBindingNamesChanged(dataInputs, dataInputsWithBindingNames)) {
      updateDataInputs(dataInputsWithBindingNames);
    }
  }, [
    dataInputs,
    flattenedOptions,
    isLoading,
    inventoryError,
    requireCodeInputNames,
    updateDataInputs,
  ]);

  const onChange = useCallback(
    (values: string[]) => {
      const newDataInputs: DataInput[] = values.map((value) => {
        const existing = dataInputs.find((d) => getDataInputOptionValue(d.config) === value);
        return existing ?? makeNewInputConfig(value, flattenedOptions);
      });

      updateDataInputs(
        requireCodeInputNames
          ? ensureCodeInputBindingNames(newDataInputs, flattenedOptions)
          : newDataInputs,
      );
    },
    [dataInputs, flattenedOptions, requireCodeInputNames, updateDataInputs],
  );

  const onPillLabelChange = useCallback(
    (newLabel: string | null, index: number) => {
      const updatedInputs = [...dataInputs];
      updatedInputs[index] = { ...updatedInputs[index], name: newLabel };
      updateDataInputs(updatedInputs);
    },
    [dataInputs, updateDataInputs],
  );

  const onPillRemove = useCallback(
    (index: number) => {
      const updatedInputs = [...dataInputs];
      updatedInputs.splice(index, 1);
      updateDataInputs(updatedInputs);
    },
    [dataInputs, updateDataInputs],
  );

  const onBindingNameChange = useCallback(
    (bindingName: string, index: number) => {
      const updatedInputs = [...dataInputs];
      updatedInputs[index] = { ...updatedInputs[index], binding_name: bindingName.trim() || null };
      updateDataInputs(updatedInputs);
    },
    [dataInputs, updateDataInputs],
  );

  const duplicateBindingIndexes = useMemo(
    () => getDuplicateBindingIndexes(dataInputs),
    [dataInputs],
  );

  const codeInputBindingRows = useMemo(() => {
    if (!showCodeInputNameFields || dataInputs.length === 0) {
      return [];
    }

    return dataInputs.map((dataInput, index) => {
      const value = getDataInputOptionValue(dataInput.config);
      const option = flattenedOptions.find((o) => o.value === value);
      const displayName =
        dataInput.name || option?.label || dataInput.last_known_display_name || `Input ${index + 1}`;
      const localError =
        getCodeInputBindingError(dataInput.binding_name) ||
        (duplicateBindingIndexes.has(index) ? 'Code variable name must be unique.' : null);

      return {
        key: `${value}-${index}-binding-name`,
        displayName,
        bindingName: dataInput.binding_name ?? '',
        error: codeInputNameErrorsByIndex[index] || localError || undefined,
        index,
      };
    });
  }, [
    codeInputNameErrorsByIndex,
    dataInputs,
    duplicateBindingIndexes,
    flattenedOptions,
    showCodeInputNameFields,
  ]);

  const hasCodeInputNameErrors = codeInputBindingRows.some((row) => row.error);

  useEffect(() => {
    if (hasCodeInputNameErrors) {
      setCodeInputNamesAccordionValue(CODE_INPUT_NAMES_ACCORDION_VALUE);
    }
  }, [hasCodeInputNameErrors]);

  useEffect(() => {
    const previousDataInputCount = previousDataInputCountRef.current;
    const dataInputWasAdded = dataInputs.length > previousDataInputCount;
    previousDataInputCountRef.current = dataInputs.length;

    if (
      showCodeInputNameFields &&
      expandCodeInputNamesOnInputAdd &&
      dataInputWasAdded &&
      dataInputs.length > 0
    ) {
      setCodeInputNamesAccordionValue(CODE_INPUT_NAMES_ACCORDION_VALUE);
    }
  }, [dataInputs.length, expandCodeInputNamesOnInputAdd, showCodeInputNameFields]);

  const codeInputNameSummary = useMemo(() => {
    if (codeInputBindingRows.length === 0) {
      return '';
    }

    const variableNames = codeInputBindingRows.map((row) => row.bindingName || 'Missing');
    const visibleVariableNames = variableNames.slice(0, 3).join(', ');
    const remainingVariableCount = variableNames.length - 3;
    const variableCountLabel = `${variableNames.length} variable${variableNames.length === 1 ? '' : 's'}`;

    return `${variableCountLabel}: ${visibleVariableNames}${
      remainingVariableCount > 0 ? `, +${remainingVariableCount}` : ''
    }`;
  }, [codeInputBindingRows]);

  const codeInputNameFields = useMemo(() => {
    if (codeInputBindingRows.length === 0) {
      return null;
    }

    return (
      <Accordion
        value={codeInputNamesAccordionValue}
        onChange={setCodeInputNamesAccordionValue}
        variant="contained"
        mt={selectedOutputContracts.length > 0 ? 0 : 'md'}
      >
        <Accordion.Item
          value={CODE_INPUT_NAMES_ACCORDION_VALUE}
          style={{
            borderColor: hasCodeInputNameErrors ? '#f34f52' : '#ececed',
          }}
        >
          <Accordion.Control>
            <Group gap={8} wrap="nowrap" justify="space-between">
              <Text size="13px" fw={600} c="#0F172A" style={{ flex: '0 0 auto' }}>
                Code variable names
              </Text>
              <Text
                size="12px"
                c={hasCodeInputNameErrors ? 'red' : 'dimmed'}
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textAlign: 'right',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {hasCodeInputNameErrors ? 'Needs attention' : codeInputNameSummary}
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              {codeInputBindingRows.map((row) => (
                <StyledTextInput
                  key={row.key}
                  label={row.displayName}
                  value={row.bindingName}
                  placeholder="input_name"
                  onChange={(event) => onBindingNameChange(event.currentTarget.value, row.index)}
                  error={row.error}
                  disabled={disabled}
                  required
                />
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    );
  }, [
    codeInputBindingRows,
    codeInputNameSummary,
    codeInputNamesAccordionValue,
    disabled,
    hasCodeInputNameErrors,
    onBindingNameChange,
    selectedOutputContracts.length,
  ]);

  const pills = useMemo(
    () =>
      dataInputs.map((dataInput, index) => {
        const value = getDataInputOptionValue(dataInput.config);
        let option = flattenedOptions.find((o) => o.value === value);
        const sourceUnavailable = unavailableSourceTypes.includes(dataInput.data_input_type);
        const missingOption = !option && !sourceUnavailable;
        if (!option) {
          // if the option is not found (e.g., deleted dataset), create a placeholder option
          const lastKnownDisplayName = dataInput.last_known_display_name || dataInput.name;
          const label =
            (sourceUnavailable ? 'Data Input Unavailable' : 'Deleted Data Input') +
            (lastKnownDisplayName ? ` (${lastKnownDisplayName})` : '');
          option = { value, label, leftSection: null };
        }

        return (
          <EditablePill
            key={`${value}-${index}`}
            value={option.label} // "value" text you show in parens - only show if different from label
            label={dataInput.name || option.label}
            onChange={(newLabel) => {
              const cleanedNewLabel = newLabel.trim() || null;
              const isSameAsOptionLabel = cleanedNewLabel === option.label;
              onPillLabelChange(isSameAsOptionLabel ? null : cleanedNewLabel, index);
            }}
            onRemove={() => onPillRemove(index)}
            leftSection={option.leftSection}
            missing={missingOption}
          />
        );
      }),
    [dataInputs, flattenedOptions, onPillLabelChange, onPillRemove, unavailableSourceTypes],
  );

  return (
    <>
      <Box pos="relative">
        <CustomLoadingOverlay visible={isLoading} />
        {inventoryError && (
          <Text size="xs" c="red" role="alert" mb="xs">
            {inventoryError}
          </Text>
        )}
        <MultiSelectWithCustomPills
          label={label || 'Data Inputs'}
          placeholder={
            showUnavailableState
              ? 'Data sources unavailable'
              : showEmptyState
                ? NO_DATA_INPUTS_PLACEHOLDER
                : placeholder || 'Select data inputs'
          }
          description={showEmptyState ? description || NO_DATA_INPUTS_DESCRIPTION : description}
          data={options}
          value={selectedOptions}
          onChange={onChange}
          pills={isLoading ? [] : pills} // show no pills while loading to avoid missing
          error={error}
          disabled={disabled || showEmptyState || showUnavailableState}
          required={required}
        />
        <DataInputSchemaContracts
          bottomSpacing={codeInputNameFields ? '14px' : undefined}
          outputContracts={selectedOutputContracts}
          showSourceLabel={selectedOutputContracts.length > 1}
        />
        {inputContractStatus && (
          <Group gap="xs" mt="4px" align="flex-start">
            <Badge color={inputContractStatus.color} variant="light" size="sm">
              {inputContractStatus.label}
            </Badge>
            <Text
              size="11px"
              c={inputContractStatus.color}
            >
              {inputContractStatus.detail}
            </Text>
          </Group>
        )}
        {inputContract?.sensitive && (
          <Group gap="xs" mt="4px" align="flex-start">
            <Badge color="violet" variant="light" size="sm">
              Sensitive dataset input
            </Badge>
            <Text size="11px" c="dimmed">
              Dataset values are handled as sensitive and are not shown here.
            </Text>
          </Group>
        )}
        {codeInputNameFields}
      </Box>
    </>
  );
};

export default MultiDataInputSelectorUI;
